import { useState } from 'react';
import { CpeDetail } from '@bumble/app/__generated__/data-contracts';
import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { Indicator } from '@bumble/app/components/Indicator';
import NoData from '@bumble/app/components/NoData';
import { Loading } from '@bumble/app/components/Loading';
import { TabGrid, TabGridStructure } from '@bumble/app/components/TabGrid';
import { theme } from '@bumble/app/theme';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import getDate from '@bumble/app/utils/get-date';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { GridColDef } from '@mui/x-data-grid';
import { useMutation } from '@tanstack/react-query';
import { CpeInterface, CpeInterfaces } from '@bumble/app/types/cpeTypes';

const accordionStyles = {
  boxShadow: 'none',
  '&::before': {
    display: 'none',
  },
};

const accordionSummaryStyles = {
  flexDirection: 'row-reverse',
  fontWeight: 700,
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
    color: theme.palette.primary.main,
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)'
  },
}

const getProvisioningExpireDate = (date?: string) => {
  if (!date) {
    return 'N/A';
  }
  return date === 'Expired' ? date : getDate(date);
};

// Renders a small, bold blue copy icon in front of the value. The value span
// needs minWidth: 0 — flex items default to min-width: auto, so without it
// long text (e.g. an address) refuses to wrap and just overflows the narrow
// TabGrid column, pushing the icon out of view.
const withCopy = (value: string | undefined | null, ariaLabel: string) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
    <Box sx={{ flexShrink: 0, mt: '1px' }}>
      <CopyToClipboard content={value || ''} ariaLabel={ariaLabel} compact />
    </Box>
    <Box component="span" sx={{ wordBreak: 'break-word', minWidth: 0, flex: '1 1 auto' }}>
      {value || 'N/A'}
    </Box>
  </Box>
);

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 100,
    disableColumnMenu: true,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
    editable: true,
    renderHeader: () => (
      <Tooltip title="Double-click a cell to edit">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>Description</span>
          <EditIcon sx={{ fontSize: 16, color: theme.palette.text.secondary, display: 'block' }} />
        </Box>
      </Tooltip>
    ),
  },
  {
    field: 'operational_state',
    headerName: 'Status',
    width: 100,
    disableColumnMenu: true,
    renderCell: (params) => {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          height: '100%'
        }}>
          {params.value === 'UP'
            ? <Indicator active={true} />
            : (params.value === 'DOWN' ? <Indicator active={false} /> : 'N/A')
          }
        </Box>
      )
    }
  },
  {
    field: 'speed',
    headerName: 'Speed',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'intf',
    headerName: 'Interface',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'cidr_addr',
    headerName: 'CIDR IP Address',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'default_gw_ip',
    headerName: 'Default Gateway IP',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'dns_ip',
    headerName: 'DNS Server IP',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'sfp_type',
    headerName: 'SFP Type',
    width: 150,
    disableColumnMenu: true,
  },
];

interface RouterDetailRow extends CpeInterface {
  id: string;
}

export const RouterTabDetails = ({ data, isLoading, mode, onInterfaceUpdated }: {
  data: CpeDetail | undefined,
  isLoading?: boolean,
  mode?: number,
  onInterfaceUpdated?: () => void,
}) => {
  const api = useApi();
  const editInterfaceDescription = useMutation(
    api.editCpeInterfaceDescriptionApiV1EditCpeInterfaceDescriptionPost,
  );
  const [generalSearchTerm, setGeneralSearchTerm] = useState('');
  // Held here, above the loading return, so open sections stay open while another CPE loads.
  const [generalExpanded, setGeneralExpanded] = useState(false);
  const [interfacesExpanded, setInterfacesExpanded] = useState(false);

  if (isLoading) return <Loading />;
  if (data === undefined) return <NoData />;

  const processRowUpdate = async (newRow: RouterDetailRow, oldRow: RouterDetailRow) => {
    if (newRow.description === oldRow.description) return oldRow;
    await editInterfaceDescription.mutateAsync({
      cpe_id: data.cpe_id,
      role: newRow.role,
      description: newRow.description || '',
    });
    onInterfaceUpdated?.();
    return newRow;
  };

  let rows: RouterDetailRow[] = [];
  const ports = data?.ports as CpeInterfaces
  for (let i in ports) {
    rows.push({
      ...ports[i],
      id: i,
    })
  }

  const wanPort = Object.values(ports ?? {}).find(
    (p) => p?.role?.toLowerCase().startsWith('wan0'),
  );

  const columnVisible = {
    name: true,
    intf: true,
    cidr_addr: true,
    default_gw_ip: true,
    dns_ip: true,
    sfp_type: data.prod_code === 'BN-CPE-1000F',
  };

  const tabDataGeneral: (TabGridStructure & { searchText?: string })[] = [
    { label: 'Device name', content: withCopy(data?.cpe_name, 'Copy device name'), searchText: data?.cpe_name || '' },
    { label: 'Product code', content: data?.prod_code || 'N/A' },
    { label: 'Account Id', content: data?.account_id || 'N/A' },
    { label: 'Device Id', content: withCopy(data?.cpe_id, 'Copy device ID'), searchText: data?.cpe_id || '' },
    { label: 'Description', content: data?.description || 'N/A' },
    { label: 'Created at', content: data !== undefined && data?.created_at ? getDate(data?.created_at) : 'N/A' },
    { label: 'Shipping address', content: withCopy(data?.shipping_address, 'Copy shipping address'), searchText: data?.shipping_address || '' },
    { label: 'Shipping track number', content: data?.tracking_number || 'N/A' },
    { label: 'Site address', content: data?.site_address || 'N/A' },
    { label: 'Local Support Contact Name', content: data?.local_support_contact_name || 'N/A' },
    { label: 'Local Support Contact Email', content: data?.local_support_contact_email || 'N/A' },
    { label: 'Local Support Contact Phone', content: data?.local_support_contact_phone || 'N/A' },
    { label: 'Netflow enabled', content: data?.netflow_enabled !== undefined && (data?.netflow_enabled == true) ? 'yes' : 'no' },
    { label: 'Admin State', content: data?.admin_state || 'N/A' },
    { label: 'OP State', content: data?.op_state !== undefined ? <Indicator active={data?.op_state === 'up'} /> : 'N/A', searchText: data?.op_state || '' },
    { label: 'Admin State Change at', content: data !== undefined && data?.admin_state_change_at ? getDate(data?.admin_state_change_at) : 'N/A' },
    { label: 'OP State Change at', content: data !== undefined && data?.op_state_change_at ? getDate(data?.op_state_change_at) : 'N/A' },
    { label: 'Provider Service ID', content: data?.provider_service_id || 'N/A' },
    { label: 'Client Circuit ID', content: data?.client_circuit_id || 'N/A' },
    { label: 'Carrier Info', content: data?.carrier_info || 'N/A' },
    { label: 'Upload Speed (Mbps)', content: wanPort?.tx_mbps != null ? String(wanPort.tx_mbps) : 'N/A' },
    { label: 'Download Speed (Mbps)', content: wanPort?.rx_mbps != null ? String(wanPort.rx_mbps) : 'N/A' },
    { label: 'Latitude', content: data?.latitude || 'N/A' },
    { label: 'Longitude', content: data?.longitude || 'N/A' },
    { label: 'Region', content: data?.state || 'N/A' },
    { label: 'Entity ID', content: data?.entity_id || 'N/A' },
    { label: 'Shipping Recipient Contact Name', content: withCopy(data?.contact_name, 'Copy shipping recipient contact name'), searchText: data?.contact_name || '' },
    { label: 'Shipping Recipient Contact Email', content: withCopy(data?.contact_email, 'Copy shipping recipient contact email'), searchText: data?.contact_email || '' },
    { label: 'Shipping Recipient Contact Phone', content: withCopy(data?.contact_phone, 'Copy shipping recipient contact phone'), searchText: data?.contact_phone || '' },
    { label: 'Down Notification Interval (min)', content:data?.scan_gap?.toString() || ''},
    { label: 'Customer Notifications Muted Until', content: data?.notification_muted_until && new Date(data.notification_muted_until.replace(' ', 'T') + (/[zZ]|[+-]\d{2}:?\d{2}$/.test(data.notification_muted_until) ? '' : 'Z')) > new Date() ? getDate(data.notification_muted_until.replace(' ', 'T') + (/[zZ]|[+-]\d{2}:?\d{2}$/.test(data.notification_muted_until) ? '' : 'Z')) : 'Not muted' },
    { label: 'MAC Address', content: data?.rj45_mac_address || 'N/A' },
    { label: 'Provisioning Token', content: data?.provisioning_token || 'N/A' },
    { label: 'Provisioning Token Expire(UTC)', content: getProvisioningExpireDate(data?.provisioning_expire_date) },
  ];

  const generalSearchTermLower = generalSearchTerm.trim().toLowerCase();
  const filteredGeneralData = generalSearchTermLower
    ? tabDataGeneral.filter(({ label, content, searchText }) => {
        const valueText = searchText ?? (typeof content === 'string' ? content : '');
        return (
          label.toLowerCase().includes(generalSearchTermLower) ||
          valueText.toLowerCase().includes(generalSearchTermLower)
        );
      })
    : tabDataGeneral;

  return (
    <Box>
      <Accordion sx={accordionStyles} expanded={generalExpanded} onChange={(_, open) => setGeneralExpanded(open)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={accordionSummaryStyles}
        >
          General
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            size="small"
            placeholder="Search fields..."
            value={generalSearchTerm}
            onChange={(e) => setGeneralSearchTerm(e.target.value)}
            sx={{ mb: 5, width: 300, maxWidth: '100%' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredGeneralData.length > 0 ? (
            <TabGrid data={filteredGeneralData} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No fields match &quot;{generalSearchTerm}&quot;.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      {mode != 1 &&
        <Accordion sx={accordionStyles} expanded={interfacesExpanded} onChange={(_, open) => setInterfacesExpanded(open)}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={accordionSummaryStyles}
          >
            Interfaces
          </AccordionSummary>
          <AccordionDetails>
            <DataTableGrid
              rows={rows}
              columns={columns}
              columnVisibilityModel={columnVisible}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={apiError}
            />
          </AccordionDetails>
        </Accordion>
      }
    </Box>
  );
};
